import { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { btn } from "../../../components/Button/buttonStyles";
import LinkedInvoicesCard from "../../../components/Payments/LinkedInvoicesCard";
import PaymentMethodEditor from "../../../components/Payments/PaymentMethodEditor";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "—";

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const Row = ({ label, value, children }) => (
  <div className="flex border-b last:border-b-0 text-sm">
    <div className="w-1/3 bg-gray-50 px-4 py-3 font-medium text-gray-700">{label}</div>
    <div className="w-2/3 px-4 py-3 text-gray-900">{children ?? value ?? "—"}</div>
  </div>
);

const SectionCard = ({ title, action, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="px-6 py-4 border-b flex items-center justify-between">
      <h3 className="text-sm font-semibold text-black-600 tracking-wide">{title}</h3>
      {action}
    </div>
    <div>{children}</div>
  </div>
);

const COVERAGE_CLS = {
  "Fully covered":     "text-green-600 font-medium",
  "Partially covered": "text-amber-600 font-medium",
  "Not covered":       "text-danger-500 font-medium",
};

const AdminVendorPaymentDetailPage = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  // Project managers get a view-only vendor payment: no update, delete, unlink
  // or messaging controls (the API rejects those writes anyway).
  const readOnly = isReadOnly(user, MODULES.VENDOR_PAYMENTS);
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [editingMethod, setEditingMethod] = useState(false);
  const [successMsg, setSuccessMsg] = useState(
    sessionStorage.getItem("paymentSuccessMsg") || ""
  );

  useEffect(() => {
    sessionStorage.removeItem("paymentSuccessMsg");
    api.get(`/project-finances/vendor-payments/${id}`, { withCredentials: true })
      .then((res) => setPayment(res.data.data))
      .catch(() => setError("Failed to fetch payment details."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="flex justify-center items-center mt-16">
      <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
    </div>
  );
  if (error) return <div className="text-center mt-10 text-danger-600 font-medium">{error}</div>;
  if (!payment) return <div className="text-center mt-10 text-gray-500">Payment not found.</div>;

  const pm = payment.paymentMethod;
  const pmType = pm?.payment_method?.replace(/_/g, " ") || "—";

  let pmName = "—";
  let pmDetails = "—";
  if (pm?.bank_transfer_detail) {
    const bt = pm.bank_transfer_detail;
    pmName = bt.payment_method_name || bt.bank_name || "—";
    const parts = [];
    if (bt.bank_name) parts.push(`Bank name: ${bt.bank_name}`);
    if (bt.bank_address) parts.push(`Bank address: ${bt.bank_address}`);
    if (bt.country) parts.push(bt.country);
    pmDetails = parts.join("\n") || "—";
  } else if (pm?.email_payment_detail) {
    pmName = pm.email_payment_detail.email || "—";
    pmDetails = pm.email_payment_detail.email || "—";
  } else if (pm?.other_payment_detail) {
    pmName = pm.other_payment_detail.payment_method_name || "—";
  }

  const coverageCls = COVERAGE_CLS[payment.coverage_by_invoices] ?? "text-gray-600";

  return (
    <div className="space-y-6">
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex justify-between items-center text-sm text-green-800">
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg("")} className="text-green-600 hover:text-green-800 font-bold">✕</button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <BackButton />
          <h2 className="text-[22px] font-semibold tracking-tight text-gray-900">Payment {payment.payment_code}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button className={btn("primary", "sm")}>
            Download as PDF
          </button>
          {/* Payments are permanent, so there is no Delete. */}
          {!readOnly && (
            <button className={btn("primary", "sm")}>
              Update
            </button>
          )}
        </div>
      </div>

      {/* Main 2-col layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left - payment details */}
        <SectionCard title="">
          <Row label="Vendor">
            {payment.vendor_id ? (
              <Link to={`/vendors/${payment.vendor_id}`} className="text-brand-600 hover:underline">
                {payment.vendorName}
              </Link>
            ) : payment.vendorName}
          </Row>
          <Row label="Total amount">
            <strong>{fmtNum(payment.amount)}</strong>
          </Row>
          <Row label="Currency" value={payment.currency || "—"} />
          <Row label="Paid at" value={fmtDate(payment.paid_at)} />

          <div className="px-4 py-3 border-b">
            <p className="text-sm font-semibold text-black-600 tracking-wide">Invoice coverage</p>
          </div>
          <Row label="Invoice coverage">
            <span className={coverageCls}>{payment.coverage_by_invoices || "Not covered"}</span>
          </Row>
          <Row label="Linked amount" value={fmtNum(payment.linked_amount)} />
          <Row label="Unlinked amount" value={fmtNum(payment.unlinked_amount)} />

          <div className="px-4 py-3 border-b">
            <p className="text-sm font-semibold text-black-600 tracking-wide">Other details</p>
          </div>
          <Row label="Legal entity" value={payment.vendor?.legal_entity || "—"} />
          <Row label="Note" value={payment.note || "—"} />
          <Row label="Created at">
            {fmtDate(payment.createdAt)}
            {payment.created_by ? <span className="ml-2 text-gray-500">| {payment.created_by}</span> : null}
          </Row>
        </SectionCard>

        {/* Right - Payment Method */}
        <SectionCard
          title="Payment Method"
          action={
            !readOnly && !editingMethod && (
              <button onClick={() => setEditingMethod(true)} className={btn("secondary", "sm")}>
                Change
              </button>
            )
          }
        >
          {editingMethod ? (
            <PaymentMethodEditor
              party="vendor"
              payment={payment}
              currentId={payment.linked_payment_method_id}
              onCancel={() => setEditingMethod(false)}
              onSaved={(updated) => {
                setPayment(updated);
                setEditingMethod(false);
              }}
            />
          ) : (
            <>
              <Row label="Name" value={pmName} />
              <Row label="Type">
                <span className="capitalize">{pmType}</span>
              </Row>
              <Row label="Details">
                <span className="whitespace-pre-line">{pmDetails}</span>
              </Row>
            </>
          )}
        </SectionCard>
      </div>

      <LinkedInvoicesCard
        party="vendor"
        payment={payment}
        currency={payment.currency}
        readOnly={readOnly}
        onChange={setPayment}
      />

      {/* Messages */}
      {!readOnly && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-sm font-semibold text-black-600 tracking-wide">Messages</h3>
          </div>
          <div className="px-6 py-4 space-y-3">
            <textarea
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full border border-gray-300 rounded p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            <button
              disabled={!message.trim()}
              className={btn("primary", "sm")}
            >
              Send message to vendor
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVendorPaymentDetailPage;
